import {useCallback, useEffect, useState} from 'react';
import {HomeStats, socialFeedApi} from '../../../../services/socialFeedApi';
import {isRequestCanceled} from '../utils';

const EMPTY_HOME_STATS: HomeStats = {
  followerCount: 0,
  followingCount: 0,
  postCount: 0,
  newMessageCount: 0,
};

export const useHomeStats = (token: string | null) => {
  const [stats, setStats] = useState<HomeStats>(EMPTY_HOME_STATS);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const fetchStats = useCallback(async (signal?: AbortSignal) => {
    if (!token) {
      setStats(EMPTY_HOME_STATS);
      setStatsLoading(false);
      setStatsError(null);
      return;
    }

    try {
      setStatsLoading(true);
      setStatsError(null);

      const response = await socialFeedApi.getHomeStats(token, signal);

      if (signal?.aborted) {
        return;
      }

      setStats(response.stats);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setStatsError(error instanceof Error ? error.message : 'Failed to load home stats');
    } finally {
      if (!signal?.aborted) {
        setStatsLoading(false);
      }
    }
  }, [token]);

  const adjustFollowingCount = useCallback((delta: number) => {
    if (!Number.isFinite(delta) || delta === 0) {
      return;
    }

    setStats((currentStats) => ({
      ...currentStats,
      followingCount: Math.max(0, currentStats.followingCount + delta),
    }));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void fetchStats(controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchStats]);

  return {
    stats,
    statsLoading,
    statsError,
    fetchStats,
    adjustFollowingCount,
  };
};
